import axios from "axios";
import { AREA_TO_DEPARTMENTS, FRENCH_DEPARTMENTS, FRENCH_REGIONS } from "../lib/frenchCities";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3000";

const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem("token")}`,
});

/**
 * Filter set produced by the AI bar.
 *
 * Every field is already validated server-side against the same vocabularies the filter
 * dropdowns are built from, so a page can apply what it recognises and ignore the rest —
 * the signals pages have no population/loyer filters, the database page has no
 * `detectionDate`, and neither has to defend against a value the model invented.
 *
 * `departments` and `regions` are the exception: they come back as the model wrote them
 * ("Moselle", "57", "Grand Est", "Lorraine") and are resolved against `lib/frenchCities` by
 * `resolveGeo` below, which stays the single source of truth.
 */
export interface AiFilters {
    cities?: string[];
    departments?: string[];
    regions?: string[];
    establishmentType?: string[];
    concept?: string[];
    nafCode?: string[];
    minRating?: number;
    minReviews?: number;
    contactFilters?: string[];
    phoneType?: ("mobile" | "landline")[];
    emailType?: ("owner" | "business")[];
    workforceRange?: string[];
    legalForm?: string[];
    ageFilter?: string;
    nbEtablissements?: string[];
    minPopulation?: number;
    maxPopulation?: number;
    minMedianM2?: number;
    maxMedianM2?: number;
    openingDateFrom?: string;
    openingDateTo?: string;
    controlResult?: string;
    controlFollowUp?: string;
    detectionDate?: string;
}

export interface AiFilterResponse {
    filters: AiFilters;
    /** One short French sentence describing what was applied. */
    explanation: string;
    /** Parts of the request no filter can express — surfaced to the user as-is. */
    unsupported: string[];
}

/** Accent/case/whitespace-insensitive comparison key, shared by geo resolution and the AI bar's autocomplete matching. */
export const normalize = (s: string) =>
    s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/[\s'’-]/g, "");

/**
 * Turn the place names the model wrote into the two filters the pages have: department codes
 * and current-region labels. Both AI fields go through here together because the model has no
 * reason to get the distinction right — "Moselle", "57", "Grand Est" and "Lorraine" are all
 * just places to a user, so each value is tried against every vocabulary rather than only the
 * one matching the field it arrived in.
 *
 * Order matters: a department wins over anything else ("Savoie" is the 73, not the historical
 * duchy), then a current region, then a former region or everyday area, which has no filter of
 * its own and is expanded into its departments ("Lorraine" → 54 · 55 · 57 · 88). Anything left
 * unmatched is dropped — an invented place must not silently widen the search.
 */
export const resolveGeo = (f: AiFilters): { departments: string[]; regions: string[] } => {
    const departments: string[] = [];
    const regions: string[] = [];
    const areaNames = Object.keys(AREA_TO_DEPARTMENTS);

    for (const raw of [...(f.departments ?? []), ...(f.regions ?? [])]) {
        const q = normalize(raw);

        const dept =
            FRENCH_DEPARTMENTS.find(d => normalize(d.code) === q) ??
            FRENCH_DEPARTMENTS.find(d => normalize(d.name) === q);
        if (dept) {
            if (!departments.includes(dept.code)) departments.push(dept.code);
            continue;
        }

        const region = FRENCH_REGIONS.find(r => normalize(r) === q);
        if (region) {
            if (!regions.includes(region)) regions.push(region);
            continue;
        }

        const area = areaNames.find(a => normalize(a) === q);
        if (area) {
            for (const code of AREA_TO_DEPARTMENTS[area]) {
                if (!departments.includes(code)) departments.push(code);
            }
        }
    }

    return { departments, regions };
};

/** How the text reached the bar — kept per call for the `/bo/ai-prompts` breakdown. */
export type AiPromptSource = "manual" | "suggestion" | "voice";

export const parseAiFilters = async (
    prompt: string,
    context: "database" | "signals",
    source: AiPromptSource = "manual"
): Promise<AiFilterResponse> => {
    const response = await axios.post(
        `${API_URL}/ai-filters/parse`,
        { prompt, context, source },
        { headers: authHeaders() }
    );
    return response.data;
};

/** A one-click opener offered under the bar, curated in `/bo/ai-prompts`. */
export interface AiSuggestion {
    prompt: string;
}

// The list changes about as often as someone edits it in the back-office, and the bar mounts
// on every visit to two different pages — so it is fetched once per context per page load,
// with in-flight calls shared rather than duplicated.
const suggestionCache = new Map<string, Promise<AiSuggestion[]>>();

export const fetchAiSuggestions = (context: "database" | "signals"): Promise<AiSuggestion[]> => {
    const cached = suggestionCache.get(context);
    if (cached) return cached;

    const request = axios
        .get(`${API_URL}/ai-filters/suggestions?context=${context}`, { headers: authHeaders() })
        .then(response => (response.data?.suggestions ?? []) as AiSuggestion[])
        .catch(() => {
            // A failed load only costs the suggestion panel, so it is not cached as a result:
            // the next mount tries again.
            suggestionCache.delete(context);
            return [] as AiSuggestion[];
        });

    suggestionCache.set(context, request);
    return request;
};

/**
 * Transcribe a recording made by the bar's mic button. The audio is posted as multipart —
 * `Content-Type` is deliberately left to the browser so it can set the multipart boundary.
 */
export const transcribeDictation = async (audio: Blob): Promise<string> => {
    const form = new FormData();
    // The extension follows the recorded container: Whisper reads it to pick a demuxer.
    const extension = audio.type.includes("mp4") ? "mp4" : audio.type.includes("ogg") ? "ogg" : "webm";
    form.append("audio", audio, `dictation.${extension}`);

    const response = await axios.post(`${API_URL}/ai-filters/transcribe`, form, { headers: authHeaders() });
    return response.data?.text ?? "";
};
